import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  CardBody,
  CardHeader,
  Chip,
  Tabs,
  Tab,
  Divider,
  Button,
  Spinner,
} from '@nextui-org/react';
import {
  Trophy,
  MapPin,
  Calendar,
  Users,
  Tv,
  Coins,
  Globe2,
  Crown,
  History,
  Star,
  ChevronRight,
  Info,
  Flag,
  Target,
  Award,
  BookOpen,
} from 'lucide-react';
import proTournamentInfoService, {
  ProTournamentInfo,
  ProTeam,
  ProKeyFact,
  ProFormatRule,
  ProPrizeBreakdown,
  ProPastWinner,
  ProScheduleEntry,
  ProRecord,
  ProNotableNineDarter,
  ProQualificationCriterion,
  ProParallelCompetition,
  ProSponsorHistoryEntry,
  ProDrawPairing,
} from '../services/proTournamentInfoService';

const SectionHeading: React.FC<{
  icon: React.ReactNode;
  eyebrow: string;
  title: string;
  description?: string | null;
}> = ({ icon, eyebrow, title, description }) => (
  <div className="mb-4 sm:mb-6 flex items-start gap-3 sm:gap-4">
    <div className="h-10 w-10 sm:h-12 sm:w-12 shrink-0 rounded-xl sm:rounded-2xl bg-[#3fced1]/10 text-[#3fced1] flex items-center justify-center">
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] sm:text-[11px] uppercase tracking-widest text-[#3fced1] font-semibold">
        {eyebrow}
      </p>
      <h2 className="text-lg sm:text-2xl md:text-3xl font-extrabold text-white leading-tight">
        {title}
      </h2>
      {description && (
        <p className="text-xs sm:text-sm text-gray-400 mt-1 max-w-2xl">
          {description}
        </p>
      )}
    </div>
  </div>
);

const StatItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}> = ({ icon, label, value, sub }) => (
  <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-zinc-900/60 border border-zinc-800 min-w-0">
    <div className="h-8 w-8 sm:h-10 sm:w-10 shrink-0 rounded-lg sm:rounded-xl bg-[#3fced1]/10 text-[#3fced1] flex items-center justify-center">
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[9px] sm:text-[11px] uppercase tracking-widest text-gray-500 truncate">
        {label}
      </p>
      <p className="text-sm sm:text-base md:text-lg font-bold text-white leading-tight break-words">
        {value}
      </p>
      {sub && (
        <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 truncate">{sub}</p>
      )}
    </div>
  </div>
);

const formatDate = (ms?: number | null): string => {
  if (!ms) return '';
  try {
    const d = new Date(ms);
    return d.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const formatDateRange = (t: ProTournamentInfo): string => {
  if (t.dateLabel) return t.dateLabel;
  const s = formatDate(t.startDate);
  const e = formatDate(t.endDate);
  if (s && e) return `${s} – ${e}`;
  return s || e || '';
};

const ProTournamentInfoPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [tournament, setTournament] = useState<ProTournamentInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!slug) return;
      setLoading(true);
      setError(null);
      try {
        const data = await proTournamentInfoService.getBySlug(slug, { expand: true });
        if (cancelled) return;
        if (!data) {
          setError('Turnier nicht gefunden');
          setTournament(null);
        } else {
          setTournament(data);
        }
      } catch (e) {
        if (!cancelled) {
          setError('Fehler beim Laden des Turniers');
          setTournament(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const keyFacts = useMemo<ProKeyFact[]>(
    () => proTournamentInfoService.getKeyFactsSorted(tournament),
    [tournament]
  );
  const formatRules = useMemo<ProFormatRule[]>(
    () => proTournamentInfoService.getFormatRulesSorted(tournament),
    [tournament]
  );
  const seededTeams = useMemo<ProTeam[]>(
    () => proTournamentInfoService.getSeededTeams(tournament),
    [tournament]
  );
  const otherTeams = useMemo<ProTeam[]>(
    () => proTournamentInfoService.getNonSeededTeams(tournament),
    [tournament]
  );
  const pastWinners = useMemo<ProPastWinner[]>(
    () => proTournamentInfoService.getPastWinnersSorted(tournament),
    [tournament]
  );
  const schedule = useMemo<ProScheduleEntry[]>(
    () => proTournamentInfoService.getScheduleSorted(tournament),
    [tournament]
  );

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-black text-white">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-black text-white px-4 text-center">
        <Trophy size={48} className="text-gray-600 mb-4" />
        <h1 className="text-xl sm:text-2xl font-bold mb-2">
          {error || 'Turnier nicht gefunden'}
        </h1>
        <p className="text-sm sm:text-base text-gray-400 mb-6">
          Das gesuchte Turnier konnte nicht geladen werden.
        </p>
        <Link to="/turniere">
          <Button className="bg-[#3fced1] text-black font-bold">
            Zurück zu den Turnieren
          </Button>
        </Link>
      </div>
    );
  }

  const t = tournament;
  const totalPrize = proTournamentInfoService.formatPrize(t);
  const dateRange = formatDateRange(t);
  const displayName = proTournamentInfoService.displayName(t);

  const hasOverview = !!(t.overview || t.intro);
  const hasFormat = proTournamentInfoService.hasSection(t, 'format');
  const hasRecords = proTournamentInfoService.hasSection(t, 'records');
  const hasTeams = proTournamentInfoService.hasSection(t, 'teams');
  const hasGroups = proTournamentInfoService.hasSection(t, 'groups');
  const hasDraw = proTournamentInfoService.hasSection(t, 'draw');
  const hasSchedule = proTournamentInfoService.hasSection(t, 'schedule');
  const hasPrize = proTournamentInfoService.hasSection(t, 'prize');
  const hasPastWinners = proTournamentInfoService.hasSection(t, 'pastWinners');
  const hasNineDarters = proTournamentInfoService.hasSection(t, 'nineDarters');
  const hasQualification = proTournamentInfoService.hasSection(t, 'qualification');
  const hasParallel = proTournamentInfoService.hasSection(t, 'parallel');
  const hasSponsorHistory = proTournamentInfoService.hasSection(t, 'sponsorHistory');
  const hasBroadcasters = proTournamentInfoService.hasSection(t, 'broadcasters');

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <Helmet>
        <title>{t.seoTitle || `${displayName} · DartCloud`}</title>
        <meta
          name="description"
          content={
            t.seoDescription ||
            t.overview ||
            t.intro ||
            `Alle Infos zu ${displayName}: Teilnehmer, Preisgeld, Spielplan, Sieger.`
          }
        />
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-800 bg-gradient-to-b from-zinc-950 via-black to-black">
        <div className="pointer-events-none absolute -top-32 -left-32 h-64 w-64 sm:h-96 sm:w-96 rounded-full bg-[#3fced1]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 right-0 h-64 w-64 sm:h-96 sm:w-96 rounded-full bg-amber-500/5 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 py-8 sm:py-12 md:py-16">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 mb-4 sm:mb-6 text-[10px] sm:text-xs text-gray-500 uppercase tracking-widest overflow-hidden">
            <Link to="/turniere" className="hover:text-[#3fced1] shrink-0">
              Turniere
            </Link>
            {t.organization && (
              <>
                <ChevronRight size={10} className="shrink-0" />
                <span className="shrink-0">{t.organization}</span>
              </>
            )}
            <ChevronRight size={10} className="shrink-0" />
            <span className="text-[#3fced1] truncate">{t.shortName || t.name}</span>
          </div>

          {/* Chips */}
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-4 sm:mb-5">
            {t.organization && (
              <Chip className="bg-[#3fced1] text-black font-bold" size="sm">
                {t.organization}
              </Chip>
            )}
            {t.edition && (
              <Chip
                className="bg-zinc-900 text-white border border-zinc-800"
                size="sm"
              >
                {t.edition}
              </Chip>
            )}
            {t.tier && (
              <Chip
                variant="bordered"
                className="border-amber-500/40 text-amber-300"
                size="sm"
              >
                {t.tier}
              </Chip>
            )}
            {t.tournamentType && (
              <Chip
                variant="bordered"
                className="border-zinc-700 text-gray-300"
                size="sm"
              >
                {t.tournamentType}
              </Chip>
            )}
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05] break-words">
            {t.name}
            {t.year && (
              <span className="block text-2xl sm:text-3xl md:text-5xl lg:text-6xl text-gray-400 font-extrabold mt-1">
                {t.year}
              </span>
            )}
          </h1>

          {(t.overview || t.intro) && (
            <p className="text-sm sm:text-base md:text-lg text-gray-300 max-w-3xl mt-4 sm:mt-5 leading-relaxed line-clamp-4 sm:line-clamp-none">
              {t.overview || t.intro}
            </p>
          )}

          {/* Key facts grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 mt-6 sm:mt-8 max-w-4xl">
            {dateRange && (
              <div className="rounded-xl sm:rounded-2xl border border-[#3fced1]/30 bg-[#3fced1]/[0.06] p-3 sm:p-4 min-w-0">
                <div className="flex items-center gap-1.5 sm:gap-2 text-[#3fced1] mb-1">
                  <Calendar size={14} className="shrink-0" />
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-widest font-bold truncate">
                    Termin
                  </span>
                </div>
                <p className="text-xs sm:text-sm md:text-base font-bold text-white leading-tight break-words">
                  {dateRange}
                </p>
              </div>
            )}

            {(t.venue || t.city) && (
              <div className="rounded-xl sm:rounded-2xl border border-zinc-800 bg-zinc-900/40 p-3 sm:p-4 min-w-0">
                <div className="flex items-center gap-1.5 sm:gap-2 text-gray-400 mb-1">
                  <MapPin size={14} className="shrink-0" />
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-widest font-bold truncate">
                    Ort
                  </span>
                </div>
                {t.venue && (
                  <p className="text-xs sm:text-sm md:text-base font-bold text-white leading-tight break-words line-clamp-2">
                    {t.venue}
                  </p>
                )}
                {t.city && (
                  <p className="text-[10px] sm:text-xs text-gray-400 truncate">
                    {t.city}
                    {t.country ? `, ${t.country}` : ''}
                  </p>
                )}
              </div>
            )}

            {totalPrize && (
              <div className="rounded-xl sm:rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-3 sm:p-4 min-w-0">
                <div className="flex items-center gap-1.5 sm:gap-2 text-amber-300 mb-1">
                  <Coins size={14} className="shrink-0" />
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-widest font-bold truncate">
                    Preisgeld
                  </span>
                </div>
                <p className="text-xs sm:text-sm md:text-base font-bold text-white break-words">
                  {totalPrize}
                </p>
                {t.trophyName && (
                  <p className="text-[10px] sm:text-xs text-gray-400 truncate">
                    {t.trophyName}
                  </p>
                )}
              </div>
            )}

            {hasBroadcasters && (
              <div className="rounded-xl sm:rounded-2xl border border-zinc-800 bg-zinc-900/40 p-3 sm:p-4 min-w-0">
                <div className="flex items-center gap-1.5 sm:gap-2 text-gray-400 mb-1">
                  <Tv size={14} className="shrink-0" />
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-widest font-bold truncate">
                    TV
                  </span>
                </div>
                <p className="text-xs sm:text-sm md:text-base font-bold text-white leading-tight truncate">
                  {t.broadcasters?.[0]?.name || ''}
                </p>
                {t.broadcasters && t.broadcasters.length > 1 && (
                  <p className="text-[10px] sm:text-xs text-gray-400 truncate">
                    +{t.broadcasters.length - 1} weitere
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 mt-6 sm:mt-8">
            {hasSchedule && (
              <Button
                className="bg-[#3fced1] text-black font-bold hover:bg-[#35b5b8] w-full sm:w-auto"
                endContent={<ChevronRight size={18} />}
                size="lg"
                onPress={() => setActiveTab('schedule')}
              >
                Spielplan ansehen
              </Button>
            )}
            <Button
              variant="bordered"
              className="border-zinc-700 text-white hover:bg-zinc-900 w-full sm:w-auto"
              startContent={<Star size={16} />}
              size="lg"
            >
              Turnier merken
            </Button>
          </div>
        </div>

        {/* Bottom highlight strip */}
        <div className="relative border-t border-zinc-900 bg-black/40 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-2 sm:gap-3 text-[11px] sm:text-xs">
            {t.titleHolderTeamName && (
              <div className="flex items-center gap-2 text-gray-400 min-w-0">
                <Crown size={14} className="text-amber-400 shrink-0" />
                <span className="truncate">
                  <span className="text-gray-500">Titelverteidiger: </span>
                  <span className="text-white font-bold">
                    {t.titleHolderTeamName}
                  </span>
                </span>
              </div>
            )}
            {t.firstHeldYear && (
              <div className="flex items-center gap-2 text-gray-400 min-w-0">
                <History size={14} className="text-amber-400 shrink-0" />
                <span className="truncate">
                  <span className="text-gray-500">Seit: </span>
                  <span className="text-white font-bold">{t.firstHeldYear}</span>
                </span>
              </div>
            )}
            {t.formatSummary && (
              <div className="flex items-center gap-2 text-gray-400 min-w-0">
                <Users size={14} className="text-[#3fced1] shrink-0" />
                <span className="truncate">
                  <span className="text-gray-500">Format: </span>
                  <span className="text-white font-bold">{t.formatSummary}</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Key Facts Stats */}
      {keyFacts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-5 sm:py-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
            {keyFacts.slice(0, 6).map((fact, idx) => (
              <StatItem
                key={`${fact.label}-${idx}`}
                icon={<Info size={18} />}
                label={fact.label || ''}
                value={fact.value || ''}
                sub={fact.sublabel || undefined}
              />
            ))}
          </div>
        </section>
      )}

      {/* Tabs */}
      <section className="max-w-7xl mx-auto px-4 pb-12 sm:pb-16">
        <Tabs
          aria-label="Turnier-Informationen"
          selectedKey={activeTab}
          onSelectionChange={(key) => setActiveTab(String(key))}
          variant="underlined"
          color="primary"
          classNames={{
            base: 'w-full',
            tabList:
              'gap-3 sm:gap-6 w-full overflow-x-auto border-b border-zinc-800 flex-nowrap scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0',
            cursor: 'bg-[#3fced1]',
            tab: 'h-11 sm:h-12 px-1 sm:px-2 whitespace-nowrap shrink-0',
            tabContent:
              'text-gray-400 group-data-[selected=true]:text-white font-semibold text-xs sm:text-sm',
          }}
        >
          {/* Überblick */}
          <Tab key="overview" title="Überblick">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mt-4 sm:mt-6">
              {hasOverview && (
                <Card className="bg-zinc-900/60 border border-zinc-800 lg:col-span-2">
                  <CardHeader className="flex gap-3 px-4 sm:px-6">
                    <div className="h-9 w-9 sm:h-10 sm:w-10 shrink-0 rounded-lg sm:rounded-xl bg-[#3fced1]/10 text-[#3fced1] flex items-center justify-center">
                      <BookOpen size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] sm:text-[11px] uppercase tracking-widest text-[#3fced1] font-semibold">
                        Das Turnier
                      </p>
                      <h3 className="text-lg sm:text-xl font-bold">Über das Event</h3>
                    </div>
                  </CardHeader>
                  <CardBody className="pt-0 px-4 sm:px-6 space-y-3 text-gray-300 text-sm leading-relaxed whitespace-pre-line break-words">
                    {t.overview || t.intro}
                  </CardBody>
                </Card>
              )}

              {hasFormat && (
                <Card className="bg-zinc-900/60 border border-zinc-800">
                  <CardHeader className="px-4 sm:px-6">
                    <div>
                      <p className="text-[10px] sm:text-[11px] uppercase tracking-widest text-[#3fced1] font-semibold">
                        Modus
                      </p>
                      <h3 className="text-lg sm:text-xl font-bold">Spieldistanzen</h3>
                    </div>
                  </CardHeader>
                  <CardBody className="pt-0 px-4 sm:px-6 space-y-2">
                    {formatRules.map((rule, idx) => (
                      <div
                        key={`${rule.label}-${idx}`}
                        className={`flex items-center justify-between gap-2 rounded-lg sm:rounded-xl px-3 py-2 ${
                          idx === formatRules.length - 1
                            ? 'bg-[#3fced1]/10 border border-[#3fced1]/30'
                            : 'bg-zinc-950/60'
                        }`}
                      >
                        <span className="text-xs sm:text-sm text-gray-300 truncate">
                          {rule.label}
                        </span>
                        <Chip
                          size="sm"
                          className={`shrink-0 ${
                            idx === formatRules.length - 1
                              ? 'bg-[#3fced1] text-black font-bold'
                              : 'bg-zinc-800 text-white'
                          }`}
                        >
                          {rule.value}
                        </Chip>
                      </div>
                    ))}
                  </CardBody>
                </Card>
              )}

              {hasRecords && (
                <Card className="bg-zinc-900/60 border border-zinc-800 lg:col-span-3">
                  <CardHeader className="px-4 sm:px-6">
                    <div>
                      <p className="text-[10px] sm:text-[11px] uppercase tracking-widest text-[#3fced1] font-semibold">
                        Rekorde & Wissenswertes
                      </p>
                      <h3 className="text-lg sm:text-xl font-bold">Zahlen & Fakten</h3>
                    </div>
                  </CardHeader>
                  <CardBody className="pt-0 px-4 sm:px-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
                      {(t.records || []).map((r: ProRecord, idx) => (
                        <div
                          key={`${r.label}-${idx}`}
                          className="rounded-lg sm:rounded-xl p-3 bg-zinc-950/60 border border-zinc-800 min-w-0"
                        >
                          <p className="text-[10px] uppercase tracking-widest text-gray-500 truncate">
                            {r.label}
                          </p>
                          <p className="text-base sm:text-lg font-bold text-white break-words">
                            {r.value}
                          </p>
                          {(r.holder || r.year || r.note) && (
                            <p className="text-xs text-gray-400 truncate">
                              {[r.holder, r.year, r.note].filter(Boolean).join(' · ')}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardBody>
                </Card>
              )}

              {hasQualification && (
                <Card className="bg-zinc-900/60 border border-zinc-800 lg:col-span-3">
                  <CardHeader className="px-4 sm:px-6">
                    <div>
                      <p className="text-[10px] sm:text-[11px] uppercase tracking-widest text-[#3fced1] font-semibold">
                        Qualifikation
                      </p>
                      <h3 className="text-lg sm:text-xl font-bold">Wie kommt man rein?</h3>
                    </div>
                  </CardHeader>
                  <CardBody className="pt-0 px-4 sm:px-6 space-y-2">
                    {(t.qualificationCriteria || []).map(
                      (q: ProQualificationCriterion, idx) => (
                        <div
                          key={`${q.label}-${idx}`}
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 rounded-lg sm:rounded-xl px-3 sm:px-4 py-3 bg-zinc-950/60 border border-zinc-800"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <Target size={16} className="text-[#3fced1] shrink-0" />
                            <p className="text-xs sm:text-sm text-white font-semibold break-words">
                              {q.label}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 sm:ml-auto">
                            {typeof q.count === 'number' && (
                              <Chip
                                size="sm"
                                className="bg-[#3fced1] text-black font-bold"
                              >
                                {q.count}
                              </Chip>
                            )}
                            <p className="text-xs sm:text-sm text-gray-300">
                              {q.value}
                            </p>
                          </div>
                        </div>
                      )
                    )}
                  </CardBody>
                </Card>
              )}
            </div>
          </Tab>

          {/* Teilnehmer */}
          {hasTeams && (
            <Tab key="teams" title={`Teilnehmer (${(t.teams || []).length})`}>
              <div className="mt-4 sm:mt-6 space-y-6">
                {seededTeams.length > 0 && (
                  <div>
                    <SectionHeading
                      icon={<Crown size={20} />}
                      eyebrow="Gesetzte Teilnehmer"
                      title="Top-Seeds"
                      description="Die bestplatzierten Spieler/Teams nach Setzliste."
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {seededTeams.map((team, idx) => {
                        const isTitleHolder =
                          !!t.titleHolderTeamName &&
                          team.name === t.titleHolderTeamName;
                        return (
                          <Card
                            key={team.id || team.name || idx}
                            className={`relative overflow-hidden border ${
                              isTitleHolder
                                ? 'bg-gradient-to-br from-[#3fced1]/15 to-zinc-900 border-[#3fced1]/40'
                                : 'bg-zinc-900/60 border-zinc-800'
                            }`}
                          >
                            <CardBody className="px-4 sm:px-6">
                              <div className="flex items-start justify-between gap-2 sm:gap-3">
                                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                  {team.flag && (
                                    <span className="text-3xl sm:text-4xl leading-none shrink-0">
                                      {team.flag}
                                    </span>
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <p className="text-[10px] sm:text-[11px] uppercase tracking-widest text-[#3fced1]">
                                      Seed #{team.seed ?? idx + 1}
                                    </p>
                                    <h4 className="text-base sm:text-xl font-extrabold text-white truncate">
                                      {team.name}
                                    </h4>
                                  </div>
                                </div>
                                {isTitleHolder && (
                                  <Chip
                                    size="sm"
                                    className="bg-amber-500 text-black font-bold shrink-0 text-[10px] sm:text-xs"
                                  >
                                    TV
                                  </Chip>
                                )}
                              </div>
                              {team.players && team.players.length > 0 && (
                                <>
                                  <Divider className="my-3 bg-zinc-800" />
                                  <div className="space-y-1.5">
                                    {team.players.map((p, pi) => (
                                      <p
                                        key={p.playerId || pi}
                                        className="text-xs sm:text-sm text-gray-300 break-words"
                                      >
                                        <span className="text-gray-500">
                                          Spieler {pi + 1}:{' '}
                                        </span>
                                        <strong className="text-white">{p.name}</strong>
                                        {p.nickname && (
                                          <span className="text-[10px] sm:text-xs text-gray-500 ml-1 block sm:inline">
                                            "{p.nickname}"
                                          </span>
                                        )}
                                      </p>
                                    ))}
                                  </div>
                                </>
                              )}
                              {team.titlesWithTeam ? (
                                <p className="text-xs text-gray-500 pt-2">
                                  <Trophy
                                    size={12}
                                    className="inline -mt-0.5 mr-1 text-amber-400"
                                  />
                                  {team.titlesWithTeam} Titel
                                </p>
                              ) : null}
                            </CardBody>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {otherTeams.length > 0 && (
                  <div>
                    <SectionHeading
                      icon={<Globe2 size={20} />}
                      eyebrow="Weitere Teilnehmer"
                      title="Restliches Feld"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                      {otherTeams.map((team, idx) => (
                        <Card
                          key={team.id || team.name || idx}
                          className="bg-zinc-900/60 border border-zinc-800"
                        >
                          <CardBody className="flex flex-row items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3">
                            {team.flag && (
                              <span className="text-2xl sm:text-3xl shrink-0">
                                {team.flag}
                              </span>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-sm sm:text-base font-bold text-white truncate">
                                {team.name}
                              </p>
                              {(team.players || []).map((p, pi) => (
                                <p
                                  key={p.playerId || pi}
                                  className="text-[11px] sm:text-xs text-gray-400 truncate"
                                >
                                  {p.name}
                                  {p.nickname ? ` "${p.nickname}"` : ''}
                                </p>
                              ))}
                            </div>
                          </CardBody>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Tab>
          )}

          {/* Auslosung */}
          {(hasGroups || hasDraw) && (
            <Tab key="draw" title="Auslosung">
              <div className="mt-4 sm:mt-6 space-y-6 sm:space-y-8">
                {hasGroups && (
                  <div>
                    <SectionHeading
                      icon={<Users size={20} />}
                      eyebrow="Gruppenphase"
                      title="Die Gruppen im Überblick"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
                      {(t.groups || []).map((g, idx) => {
                        const teamsInGroup = (t.teams || []).filter((tm) =>
                          (g.teamIds || []).includes(tm.id || '')
                        );
                        return (
                          <Card
                            key={g.name || idx}
                            className="bg-zinc-900/60 border border-zinc-800"
                          >
                            <CardHeader className="pb-2 px-3 sm:px-4">
                              <p className="font-bold text-[#3fced1] text-sm tracking-wider">
                                {g.name}
                              </p>
                            </CardHeader>
                            <CardBody className="pt-0 px-3 sm:px-4 space-y-1.5 sm:space-y-2">
                              {teamsInGroup.map((team, ti) => (
                                <div
                                  key={team.id || ti}
                                  className="flex items-center gap-2 py-1.5 px-2 rounded-lg bg-zinc-950/60"
                                >
                                  <span className="text-[10px] sm:text-xs text-gray-500 w-4 shrink-0">
                                    {ti + 1}.
                                  </span>
                                  {team.flag && (
                                    <span className="text-base sm:text-xl shrink-0">
                                      {team.flag}
                                    </span>
                                  )}
                                  <span className="text-xs sm:text-sm text-white font-medium truncate">
                                    {team.name}
                                  </span>
                                </div>
                              ))}
                            </CardBody>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {hasDraw && (
                  <div>
                    <SectionHeading
                      icon={<Trophy size={20} />}
                      eyebrow="Auslosung"
                      title="Draw / Erste Runde"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      {(t.drawPairings || [])
                        .slice()
                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                        .map((pair: ProDrawPairing, idx) => (
                          <div
                            key={idx}
                            className="rounded-lg sm:rounded-xl border border-zinc-800 bg-zinc-900/60 overflow-hidden text-sm"
                          >
                            {pair.round && (
                              <div className="px-3 py-1.5 bg-zinc-950/60 text-[10px] uppercase tracking-widest text-gray-500 font-semibold flex justify-between gap-2">
                                <span className="truncate">{pair.round}</span>
                                <span className="shrink-0">
                                  #{(pair.order ?? idx) + 1}
                                </span>
                              </div>
                            )}
                            <div className="px-3 py-2 border-b border-zinc-800 text-gray-200 font-semibold text-xs sm:text-sm truncate">
                              {pair.homeTeamName || '—'}
                            </div>
                            <div className="px-3 py-2 text-gray-400 font-semibold text-xs sm:text-sm truncate">
                              {pair.awayTeamName || '—'}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </Tab>
          )}

          {/* Spielplan */}
          {hasSchedule && (
            <Tab key="schedule" title="Spielplan">
              <div className="mt-4 sm:mt-6">
                <SectionHeading
                  icon={<Calendar size={20} />}
                  eyebrow={dateRange}
                  title="Sessions & Spieltage"
                />
                <div className="space-y-2 sm:space-y-3">
                  {schedule.map((s, idx) => (
                    <Card
                      key={idx}
                      className={`border ${
                        idx === schedule.length - 1
                          ? 'bg-gradient-to-r from-[#3fced1]/15 to-zinc-900 border-[#3fced1]/40'
                          : 'bg-zinc-900/60 border-zinc-800'
                      }`}
                    >
                      <CardBody className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 sm:py-4">
                        <div className="flex items-center gap-3 sm:w-48 shrink-0">
                          <div
                            className={`h-10 w-10 sm:h-12 sm:w-12 rounded-lg sm:rounded-xl flex items-center justify-center font-bold text-base sm:text-lg shrink-0 ${
                              idx === schedule.length - 1
                                ? 'bg-[#3fced1] text-black'
                                : 'bg-zinc-800 text-[#3fced1]'
                            }`}
                          >
                            {idx + 1}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm sm:text-base font-bold text-white truncate">
                              {s.dateLabel || formatDate(s.date)}
                            </p>
                            {s.sessionLabel && (
                              <p className="text-[11px] sm:text-xs text-gray-400 truncate">
                                {s.sessionLabel}
                              </p>
                            )}
                          </div>
                        </div>

                        <Divider
                          orientation="vertical"
                          className="hidden sm:block h-12 bg-zinc-800"
                        />

                        <div className="flex-1 min-w-0">
                          <p className="text-sm sm:text-base font-bold text-white break-words">
                            {s.round || s.description || s.sessionLabel}
                          </p>
                          {s.description && s.round && (
                            <p className="text-xs sm:text-sm text-gray-400 break-words">
                              {s.description}
                            </p>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </div>
            </Tab>
          )}

          {/* Preisgeld */}
          {hasPrize && (
            <Tab key="prize" title="Preisgeld">
              <div className="mt-4 sm:mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                <Card className="lg:col-span-2 bg-zinc-900/60 border border-zinc-800">
                  <CardHeader className="px-4 sm:px-6">
                    <div>
                      <p className="text-[10px] sm:text-[11px] uppercase tracking-widest text-[#3fced1] font-semibold">
                        Aufschlüsselung
                      </p>
                      <h3 className="text-lg sm:text-xl font-bold">
                        Preisgeld pro Runde
                      </h3>
                    </div>
                  </CardHeader>
                  <CardBody className="pt-0 px-4 sm:px-6">
                    <div className="space-y-2">
                      {(t.prizeBreakdown || []).map(
                        (p: ProPrizeBreakdown, idx) => (
                          <div
                            key={`${p.position}-${idx}`}
                            className={`flex items-center justify-between gap-3 sm:gap-4 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 border ${
                              idx === 0
                                ? 'bg-gradient-to-r from-amber-500/20 to-zinc-900 border-amber-500/40'
                                : 'bg-zinc-950/60 border-zinc-800'
                            }`}
                          >
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                              {idx === 0 && (
                                <Trophy
                                  size={18}
                                  className="text-amber-400 shrink-0"
                                />
                              )}
                              <div className="min-w-0">
                                <p className="text-xs sm:text-base font-bold text-white truncate">
                                  {p.position}
                                </p>
                                {p.count && (
                                  <p className="text-[10px] sm:text-xs text-gray-500">
                                    {p.count}× vergeben
                                  </p>
                                )}
                              </div>
                            </div>
                            <p
                              className={`text-sm sm:text-lg font-extrabold shrink-0 text-right ${
                                idx === 0 ? 'text-amber-300' : 'text-white'
                              }`}
                            >
                              {proTournamentInfoService.formatAmount(
                                p.amount,
                                p.currency
                              )}
                              {p.perTeam && (
                                <span className="text-[10px] sm:text-xs font-medium text-gray-400 ml-1 block sm:inline">
                                  / Team
                                </span>
                              )}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </CardBody>
                </Card>

                <Card className="bg-gradient-to-br from-amber-500/20 via-zinc-900 to-zinc-950 border border-amber-500/30">
                  <CardBody className="text-center py-6 sm:py-10 px-4 sm:px-6">
                    <Coins
                      size={32}
                      className="text-amber-300 mx-auto mb-2"
                    />
                    <p className="text-[10px] sm:text-[11px] uppercase tracking-widest text-amber-300 font-semibold">
                      Gesamtpreisgeld {t.year || ''}
                    </p>
                    <p className="text-3xl sm:text-5xl font-black text-white mt-1 break-words">
                      {totalPrize}
                    </p>
                    {t.trophyName && (
                      <>
                        <Divider className="my-4 sm:my-6 bg-zinc-800" />
                        <Award
                          size={22}
                          className="text-amber-300 mx-auto mb-2"
                        />
                        <p className="text-xs text-gray-400">Trophäe</p>
                        <p className="text-base sm:text-lg font-bold text-white break-words">
                          {t.trophyName}
                        </p>
                      </>
                    )}
                  </CardBody>
                </Card>

                {hasSponsorHistory && (
                  <Card className="lg:col-span-3 bg-zinc-900/60 border border-zinc-800">
                    <CardHeader className="px-4 sm:px-6">
                      <div>
                        <p className="text-[10px] sm:text-[11px] uppercase tracking-widest text-[#3fced1] font-semibold">
                          Historie
                        </p>
                        <h3 className="text-lg sm:text-xl font-bold">
                          Sponsoren & Preisgeld
                        </h3>
                      </div>
                    </CardHeader>
                    <CardBody className="pt-0 px-2 sm:px-6">
                      {/* Mobile: Card-Liste */}
                      <div className="sm:hidden space-y-2">
                        {(t.sponsorHistory || [])
                          .slice()
                          .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))
                          .map((s: ProSponsorHistoryEntry, idx) => (
                            <div
                              key={`${s.year}-${idx}`}
                              className="flex items-center justify-between gap-3 p-3 rounded-lg bg-zinc-950/60 border border-zinc-800"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-bold text-white">
                                  {s.year}
                                </p>
                                <p className="text-[11px] text-gray-400 truncate">
                                  {s.sponsor || '—'}
                                </p>
                              </div>
                              <p className="text-xs font-semibold text-amber-300 shrink-0">
                                {proTournamentInfoService.formatAmount(
                                  s.prizeMoney,
                                  t.prizeMoneyCurrency
                                ) || '—'}
                              </p>
                            </div>
                          ))}
                      </div>

                      {/* Desktop: Tabelle */}
                      <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-zinc-950/60">
                            <tr className="text-left">
                              <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-gray-400 font-semibold">
                                Jahr
                              </th>
                              <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-gray-400 font-semibold">
                                Sponsor
                              </th>
                              <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-gray-400 font-semibold">
                                Preisgeld
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {(t.sponsorHistory || [])
                              .slice()
                              .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))
                              .map((s: ProSponsorHistoryEntry, idx) => (
                                <tr
                                  key={`${s.year}-${idx}`}
                                  className="border-t border-zinc-800 hover:bg-zinc-900/60"
                                >
                                  <td className="px-3 py-2 font-bold text-white">
                                    {s.year}
                                  </td>
                                  <td className="px-3 py-2 text-gray-300">
                                    {s.sponsor || '—'}
                                  </td>
                                  <td className="px-3 py-2 text-gray-300">
                                    {proTournamentInfoService.formatAmount(
                                      s.prizeMoney,
                                      t.prizeMoneyCurrency
                                    ) || '—'}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>
                )}
              </div>
            </Tab>
          )}

          {/* Vergangene Sieger */}
          {hasPastWinners && (
            <Tab key="history" title="Sieger">
              <div className="mt-4 sm:mt-6">
                <SectionHeading
                  icon={<History size={20} />}
                  eyebrow="Hall of Fame"
                  title={`Sieger ${
                    t.firstHeldYear ? `seit ${t.firstHeldYear}` : 'der Vorjahre'
                  }`}
                />

                {/* Mobile: Card-Layout */}
                <div className="sm:hidden space-y-2">
                  {pastWinners.map((w, idx) => (
                    <Card
                      key={`${w.year}-${idx}`}
                      className={`border ${
                        idx === 0
                          ? 'bg-amber-500/5 border-amber-500/30'
                          : 'bg-zinc-900/60 border-zinc-800'
                      }`}
                    >
                      <CardBody className="px-3 py-3 space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-white text-base">
                              {w.year}
                            </span>
                            {idx === 0 && (
                              <Chip
                                size="sm"
                                className="bg-amber-500 text-black font-bold text-[10px]"
                              >
                                aktuell
                              </Chip>
                            )}
                          </div>
                          {w.score && (
                            <Chip
                              size="sm"
                              className="bg-zinc-800 text-[#3fced1] font-bold text-[10px]"
                            >
                              {w.score}
                            </Chip>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Trophy
                            size={14}
                            className="text-amber-400 shrink-0"
                          />
                          <p className="text-sm font-bold text-white break-words">
                            {w.winnerTeamName}
                          </p>
                        </div>
                        {w.runnerUpTeamName && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 shrink-0">
                              vs.
                            </span>
                            <p className="text-xs text-gray-400 break-words">
                              {w.runnerUpTeamName}
                            </p>
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  ))}
                </div>

                {/* Desktop: Tabelle */}
                <div className="hidden sm:block overflow-hidden rounded-2xl border border-zinc-800">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-900/80">
                      <tr className="text-left">
                        <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-gray-400 font-semibold">
                          Jahr
                        </th>
                        <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-gray-400 font-semibold">
                          Sieger
                        </th>
                        <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-gray-400 font-semibold">
                          Finalist
                        </th>
                        <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-gray-400 font-semibold">
                          Ergebnis
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pastWinners.map((w, idx) => (
                        <tr
                          key={`${w.year}-${idx}`}
                          className={`border-t border-zinc-800 transition-colors hover:bg-zinc-900/60 ${
                            idx === 0 ? 'bg-amber-500/5' : ''
                          }`}
                        >
                          <td className="px-4 py-3 font-bold text-white">
                            {w.year}
                            {idx === 0 && (
                              <Chip
                                size="sm"
                                className="ml-2 bg-amber-500 text-black font-bold"
                              >
                                aktuell
                              </Chip>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-bold text-white">
                              {w.winnerTeamName}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-400">
                            {w.runnerUpTeamName || '—'}
                          </td>
                          <td className="px-4 py-3">
                            {w.score && (
                              <Chip
                                size="sm"
                                className="bg-zinc-800 text-[#3fced1] font-bold"
                              >
                                {w.score}
                              </Chip>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Tab>
          )}

          {/* 9-Darter */}
          {hasNineDarters && (
            <Tab key="ninedarters" title="9-Darter">
              <div className="mt-4 sm:mt-6">
                <SectionHeading
                  icon={<Target size={20} />}
                  eyebrow="Highlights"
                  title="9-Darter-Geschichte"
                  description="Alle perfekten Aufnahmen in der Geschichte des Turniers."
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                  {(t.notableNineDarters || [])
                    .slice()
                    .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))
                    .map((n: ProNotableNineDarter, idx) => (
                      <Card
                        key={idx}
                        className="bg-zinc-900/60 border border-zinc-800"
                      >
                        <CardBody className="flex flex-row items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3">
                          <div className="h-12 w-12 sm:h-14 sm:w-14 shrink-0 rounded-xl sm:rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center font-extrabold text-sm sm:text-lg">
                            {n.year}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm sm:text-base font-bold text-white truncate">
                              {n.playerName}
                            </p>
                            {n.opponent && (
                              <p className="text-[11px] sm:text-xs text-gray-400 truncate">
                                vs. {n.opponent}
                              </p>
                            )}
                            {n.round && (
                              <p className="text-[11px] sm:text-xs text-[#3fced1] truncate">
                                {n.round}
                              </p>
                            )}
                            {n.note && (
                              <p className="text-[10px] sm:text-xs text-gray-500 mt-1 line-clamp-2">
                                {n.note}
                              </p>
                            )}
                          </div>
                          <Target
                            size={22}
                            className="text-amber-400 shrink-0"
                          />
                        </CardBody>
                      </Card>
                    ))}
                </div>
              </div>
            </Tab>
          )}

          {/* Parallele Wettbewerbe */}
          {hasParallel && (
            <Tab key="parallel" title="Parallel">
              <div className="mt-4 sm:mt-6">
                <SectionHeading
                  icon={<Flag size={20} />}
                  eyebrow="Begleitende Turniere"
                  title="Parallel-Wettbewerbe"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  {(t.parallelCompetitions || []).map(
                    (c: ProParallelCompetition, idx) => (
                      <Card
                        key={`${c.name}-${idx}`}
                        className="bg-zinc-900/60 border border-zinc-800"
                      >
                        <CardHeader className="px-4 sm:px-6">
                          <h3 className="text-base sm:text-lg font-bold text-white break-words">
                            {c.name}
                          </h3>
                        </CardHeader>
                        <CardBody className="pt-0 px-4 sm:px-6 space-y-2 text-xs sm:text-sm">
                          {c.description && (
                            <p className="text-gray-300 break-words">
                              {c.description}
                            </p>
                          )}
                          {c.prizeMoneyLabel && (
                            <p className="text-gray-400 break-words">
                              <Coins
                                size={14}
                                className="inline -mt-0.5 mr-1 text-amber-400"
                              />
                              Preisgeld:{' '}
                              <span className="text-white font-bold">
                                {c.prizeMoneyLabel}
                              </span>
                            </p>
                          )}
                          {c.winnerName && (
                            <p className="text-gray-400 break-words">
                              <Crown
                                size={14}
                                className="inline -mt-0.5 mr-1 text-amber-400"
                              />
                              Letzter Sieger:{' '}
                              <span className="text-white font-bold">
                                {c.winnerName}
                              </span>
                            </p>
                          )}
                        </CardBody>
                      </Card>
                    )
                  )}
                </div>
              </div>
            </Tab>
          )}
        </Tabs>
      </section>
    </div>
  );
};

export default ProTournamentInfoPage;
